import { getChanges, type Entity, type Folder } from './api.js';
import type { Cache, CachedEdge, CachedMembership } from './cache.js';

/**
 * La synchronisation incrémentale — **le drainage de `/changes`**.
 *
 * Une page porte cinq natures d'items, distinguées par leur `kind`, et le client
 * n'a **pas** à les deviner à partir des attributs présents (api.md §0) :
 *
 * - `entity` et `album` — ce qu'on possède, avec son `Deleted` ;
 * - `folder` — l'arborescence, dont la disparition doit voyager elle aussi ;
 * - `membership` — **les groupes où l'on est**, et surtout ceux d'où l'on sort ;
 * - `edge` — **ce qui nous est partagé**, projeté et affichable tel quel (D28).
 *
 * ### Les deux dernières étaient jetées, et c'était **D-23**
 *
 * Jusqu'à 4.6, elles étaient écartées sciemment : l'étape 3 n'affichait que ce
 * qu'on possède. Les écarter n'était pas les reporter — **le curseur avançait sur
 * la page entière**, donc ces items ne seraient jamais reproposés à un client déjà
 * synchronisé. La dette n'était pas « il manque un écran », c'était « la donnée est
 * passée et ne repassera pas ».
 *
 * La reprise n'est donc pas un correctif mais un **premier plein** : le schéma du
 * cache passe à 2, la migration efface le curseur, le serveur répond `full: true`
 * et le miroir est remplacé. Rien ici n'a à le savoir — c'est ce qui rend la chose
 * sûre plutôt qu'astucieuse.
 *
 * **Le curseur n'avance qu'une fois la page écrite** (cause R7) : c'est la
 * transaction du cache qui le porte, et un échec d'écriture laisse la page à
 * reprendre plutôt qu'un trou définitif.
 */

export interface SyncResult {
  readonly entities: Entity[];
  readonly folders: Folder[];
  readonly edges: CachedEdge[];
  readonly memberships: CachedMembership[];
  /** Nombre de pages drainées — zéro veut dire « rien de neuf ». */
  readonly pages: number;
}

/**
 * Le serveur annonce une suite mais ne fait avancer ni le curseur ni les items :
 * le redemander rendrait la même page, sans fin. C'est la seule condition qui
 * prouve qu'on tourne en rond — elle remplace la borne de 50 pages.
 */
export class SyncStalled extends Error {
  constructor(readonly pages: number) {
    super(`sync_stalled (page ${pages})`);
  }
}

/** Où en est un drainage — ce que le bandeau affiche pendant qu'il dure. */
export interface SyncProgress {
  readonly pages: number;
  readonly items: number;
}

/** Ce dont le drainage a besoin, et rien de plus — les mesures en branchent des doubles. */
export interface SyncPorts {
  readonly changes?: typeof getChanges;
  readonly onProgress?: (progress: SyncProgress) => void;
}

/**
 * La clé d'une arête : **le groupe et la cible**, jamais la cible seule.
 *
 * Le même objet peut être partagé à deux groupes, et deux arêtes le portent alors —
 * une par partition. Les confondre sous une clé unique ferait disparaître l'objet
 * du second groupe dès qu'on quitte le premier.
 */
const edgeKey = (container: string, id: string): string => `${container}|${id}`;

/**
 * Draine `/changes` **jusqu'au bout** *(2026-09-26, comme l'app en AV1)*.
 *
 * `MAX_PAGES = 50` arrêtait un drainage à 10 000 items **sans le dire** : le
 * curseur était écrit, donc rien n'était perdu, mais un navigateur neuf face à
 * une photothèque sauvegardée par l'app n'en montrait qu'une partie jusqu'au
 * rechargement suivant — et rien ne distinguait « incomplet » de « tout ». Ce qui
 * la remplace : la **progression** après chaque page écrite, et la garde
 * `SyncStalled` contre la seule boucle infinie possible.
 */
export async function sync(
  cache: Pick<Cache, 'apply' | 'read'>,
  from: string | undefined,
  ports: SyncPorts = {},
): Promise<SyncResult> {
  const changes = ports.changes ?? getChanges;
  let cursor = from;
  let pages = 0;
  let received = 0;

  for (;;) {
    const before = cursor;
    const page = await changes(cursor);
    pages += 1;
    received += page.items.length;

    const entities: { key: string; value: Entity | null }[] = [];
    const folders: { key: string; value: Folder | null }[] = [];
    const edges: { key: string; value: CachedEdge | null }[] = [];
    const memberships: { key: string; value: CachedMembership | null }[] = [];
    const dropped: string[] = [];

    for (const raw of page.items) {
      const kind = raw['kind'];

      if (kind === 'entity' || kind === 'album') {
        const entity = raw as unknown as Entity;
        const key = entity.Fid ?? entity.Aid;
        if (!key) continue;
        // Un tombstone **est** l'information : c'est la seule façon dont un
        // appareil déjà synchronisé apprend une suppression (I-12).
        entities.push({ key, value: raw['Deleted'] === true ? null : entity });
        continue;
      }

      if (kind === 'folder') {
        const folder = String(raw['Folder'] ?? '');
        if (!folder) continue;
        folders.push({
          key: folder,
          value:
            raw['Deleted'] === true
              ? null
              : {
                  folder,
                  itemCount: Number(raw['ItemCount'] ?? 0),
                  updatedAt: String(raw['UpdatedAt'] ?? ''),
                },
        });
        continue;
      }

      // **C'est par là — et par là seule — qu'on apprend qu'on a été ajouté à un
      // groupe, ou qu'on en a été retiré** (R13). L'item Membre entre dans GSI2 sur
      // la partition de l'utilisateur, jamais sur celle du groupe : un ex-membre
      // cesse d'interroger `GRP#<gid>` à la seconde où il perd l'appartenance, donc
      // un signal placé là ne l'atteindrait jamais.
      if (kind === 'membership') {
        const gid = String(raw['Gid'] ?? '');
        if (!gid) continue;
        if (raw['Deleted'] === true) {
          memberships.push({ key: gid, value: null });
          // La purge locale de D17 : ce que le groupe portait cesse d'être lisible.
          dropped.push(gid);
        } else {
          memberships.push({ key: gid, value: raw as unknown as CachedMembership });
        }
        continue;
      }

      if (kind === 'edge') {
        const container = String(raw['container'] ?? '');
        const id = String(raw['Fid'] ?? raw['Aid'] ?? '');
        if (!container || !id) continue;
        edges.push({
          key: edgeKey(container, id),
          // Un tombstone d'arête est une **révocation** : l'objet existe toujours
          // chez son propriétaire, il a cessé d'être partagé avec nous.
          value: raw['Deleted'] === true ? null : (raw as unknown as CachedEdge),
        });
      }
    }

    await cache.apply({
      entities,
      folders,
      edges,
      memberships,
      dropped,
      cursor: page.cursor,
      full: page.full,
    });
    cursor = page.cursor;
    ports.onProgress?.({ pages, items: received });

    // `hasMore` permet de vider un arriéré tout de suite, plutôt que d'attendre
    // le réveil suivant — c'est exactement ce pour quoi il existe. Une page vide
    // dont le curseur avance est normale : le serveur a passé une fenêtre
    // entièrement filtrée (AP19).
    if (!page.hasMore) break;
    if (page.items.length === 0 && page.cursor === before) throw new SyncStalled(pages);
  }

  const state = await cache.read();
  return {
    entities: state.entities,
    folders: state.folders,
    edges: state.edges,
    memberships: state.memberships,
    pages,
  };
}
